import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit3,
  Lock,
  BadgeCheck,
  Eye,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import SettingItem from "./ui/SettingItem";
import ProfileDetailedView from "./ProfileDetailedView";
import ChangeUsernameView from "./ChangeUsernameView";
import ChangePasswordView from "./ChangePasswordView";

// Utility function to get initials from name
const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  if (!imageUrls) return null;

  // Handle if imageUrls is a string (direct URL)
  if (typeof imageUrls === "string") {
    return (
      <img
        src={imageUrls}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    );
  }

  // Handle if imageUrls is an object with different sizes
  const small = imageUrls.small || imageUrls.medium || imageUrls.large;
  const medium = imageUrls.medium || imageUrls.large || small;
  const large = imageUrls.large || medium || small;

  return (
    <picture>
      <source media="(max-width: 768px)" srcSet={`${small} 1x, ${medium} 2x`} />
      <source media="(min-width: 769px)" srcSet={`${medium} 1x, ${large} 2x`} />
      <img
        src={medium}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

// Constants for view states
const VIEWS = {
  MAIN: "main",
  PROFILE: "profile",
  USERNAME: "username",
  PASSWORD: "password",
};

// Utility functions for data transformation
const transformUserData = (user) => {
  const profile = user?.profile || {};

  return {
    id: user?.id,
    email: user?.email,
    name: profile.name || "",
    username: profile.username,
    birthday: profile.birthday,
    isVerified: profile.isVerified,
    gender: profile.gender || "",
    profilePicture: profile.profilePicture || null,
    bio: profile.bio || "",
    location: profile.location || "",
    website: profile.website || "",
    currentCompany: profile.currentCompany || "",
    yearOfExperience: profile.yearOfExperience || "",
    education: profile.education || "",
    position: profile.position || "",
    socialLinks: profile.socialLinks || {},
    followersCount: profile.followersCount ?? 0,
    followingCount: profile.followingCount ?? 0,
    postsCount: profile.postsCount ?? 0,
    privacySettings: profile.privacySettings ?? 0,
  };
};

const transformUserForDetailedView = (userData) => ({
  name: userData.name,
  username: userData.username,
  email: userData.email,
  bio: userData.bio,
  location: userData.location,
  website: userData.website,
  gender: userData.gender,
  birthday: userData.birthday,
  isVerified: userData.isVerified,
  profilePicture: userData.profilePicture || null,
  workExperience: {
    currentCompany: userData.currentCompany || "",
    position: userData.position || "",
    yearsOfExperience: userData.yearOfExperience || "",
  },
  education: userData.education,
  socialProfiles: userData.socialLinks || {},
  followersCount: userData.followersCount,
  followingCount: userData.followingCount,
  postsCount: userData.postsCount,
  privacySettings: userData.privacySettings,
});

const ProfileAccountSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState(VIEWS.MAIN);

  const userData = useMemo(() => transformUserData(user), [user]);

  const handleViewChange = (view) => setCurrentView(view);
  const handleEditProfile = () => navigate("/edit-profile");
  const handleUsernameUpdate = (newUsername) => {};

  const renderView = () => {
    switch (currentView) {
      case VIEWS.PROFILE:
        return (
          <ProfileDetailedView
            userData={transformUserForDetailedView(userData)}
            onBack={() => handleViewChange(VIEWS.MAIN)}
          />
        );
      case VIEWS.USERNAME:
        return (
          <ChangeUsernameView
            currentUsername={userData.username}
            onBack={() => handleViewChange(VIEWS.MAIN)}
            onUpdate={handleUsernameUpdate}
          />
        );
      case VIEWS.PASSWORD:
        return (
          <ChangePasswordView onBack={() => handleViewChange(VIEWS.MAIN)} />
        );
      case VIEWS.MAIN:
      default:
        return (
          <div className="min-h-screen bg-white">
            {/* Main Content */}
            <div className="max-w-3xl mx-auto px-4 py-8">
              {/* Profile Picture Section - Centered */}
              <div className="flex justify-center mb-4">
                <div className="relative w-32 h-32">
                  {userData.profilePicture ? (
                    <ResponsiveProfileImage
                      imageUrls={userData.profilePicture}
                      alt={userData.name}
                      className="w-full h-full rounded-full object-cover shadow-md"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-slate-300">
                      {userData.name ? (
                        <span className="text-slate-600 font-bold text-3xl">
                          {getInitials(userData.name)}
                        </span>
                      ) : (
                        <svg
                          className="w-full h-full text-slate-400"
                          viewBox="0 0 100 100"
                          fill="currentColor"
                        >
                          <circle cx="50" cy="35" r="18" />
                          <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Profile Info - Centered */}
              <div className="text-center mb-8">
                {/* Name */}
                <h2 className="text-2xl font-semibold text-black">
                  {userData.name}
                </h2>

                {/* Username + Verified Badge */}
                <div className="flex items-center justify-center gap-1 mb-1">
                  <p className="text-gray-700 font-medium">
                    {userData.username.length > 20
                      ? userData.username.slice(0, 20) + "..."
                      : userData.username}
                  </p>
                  {userData.isVerified && (
                    <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />
                  )}
                </div>

                {/* Bio */}
                {userData.bio && (
                  <p className="text-gray-600 text-sm mt-2">{userData.bio}</p>
                )}
              </div>

              {/* Actions Section */}
              <div className="space-y-2 w-full max-w-3xl mx-auto">
                <SettingItem
                  label="View Profile"
                  icon={Eye}
                  onClick={() => handleViewChange(VIEWS.PROFILE)}
                />
                <SettingItem
                  label="Edit Profile"
                  icon={Edit3}
                  onClick={handleEditProfile}
                />
                <SettingItem
                  label="Change Password"
                  icon={Lock}
                  onClick={() => handleViewChange(VIEWS.PASSWORD)}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  return renderView();
};

export default ProfileAccountSection;